import { createUseStyles } from 'react-jss';
import * as layout from 'foundation/layout';
import * as colors from 'foundation/colors';
import * as typography from 'foundation/typography';
import { TagColor } from 'types/enums';

interface DynamicProps {
    hasOnClickFunction?: boolean;
    hasLeftIcon?: boolean;
    hasRightIcon?: boolean;
}

// Helper function to get the text color for a tag
export const getTagTextColor = (type: TagColor, contrast?: boolean, disabled?: boolean): string => {
    if (disabled) {
        return colors.black.disabled;
    }

    switch (type) {
        case TagColor.RED:
            return contrast ? colors.neutral.white : colors.red._900;
        case TagColor.GREEN:
            return contrast ? colors.neutral.white : colors.green._900;
        case TagColor.BLUE:
            return contrast ? colors.neutral.white : colors.blue.prussian;
        case TagColor.YELLOW:
            return contrast ? colors.black.highEmphasis : colors.yellow._900;
        case TagColor.GREY:
            return contrast ? colors.neutral.white : colors.black.highEmphasis;
        case TagColor.WHITE:
            return colors.white.highEmphasis;
        default:
            return colors.black.highEmphasis;
    }
};

const useStyles = createUseStyles({
    container: ({ hasLeftIcon, hasRightIcon }: DynamicProps) => ({
        ...typography.caption._1.bold,
        display: 'flex',
        borderRadius: 4,
        gap: layout.baseSpacing / 2,
        whiteSpace: 'nowrap',
        alignItems: 'center',
        width: 'fit-content',
        justifyContent: 'center',
        padding: [
            layout.baseSpacing / 2,
            hasRightIcon ? layout.baseSpacing / 2 : layout.baseSpacing,
            layout.baseSpacing / 2,
            hasLeftIcon ? layout.baseSpacing / 2 : layout.baseSpacing,
        ],
    }),
    tagLoadingContainer: {
        width: layout.baseSpacing * 2,
        height: layout.baseSpacing * 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    redContrast: {
        background: colors.red._500,
        color: colors.neutral.white,
        '&:hover': {
            background: colors.red._600,
        },
        '&:active': {
            background: colors.red._700,
        },
    },
    red: {
        background: colors.red._50,
        color: colors.red._900,
        '&:hover': {
            background: colors.red._100,
        },
        '&:active': {
            background: colors.red._200,
        },
    },
    greenContrast: {
        background: colors.green._700,
        color: colors.neutral.white,
        '&:hover': {
            background: colors.green._800,
        },
        '&:active': {
            background: colors.green._900,
        },
    },
    green: {
        background: colors.green._50,
        color: colors.green._900,
        '&:hover': {
            background: colors.green._100,
        },
        '&:active': {
            background: colors.green._200,
        },
    },
    whiteContrast: {
        background: colors.stateOverlay.white.focus,
        color: colors.white.highEmphasis,
    },
    white: {
        background: colors.stateOverlay.white.focus,
        color: colors.white.highEmphasis,
    },
    blueContrast: {
        background: colors.primary,
        color: colors.neutral.white,
        '&:hover': {
            background: colors.blue.prussian,
        },
        '&:active': {
            background: colors.blue.marine,
        },
    },
    blue: {
        background: colors.blue.background,
        color: colors.blue.prussian,
        '& svg': {
            color: colors.blue.prussian,
        },
        '&:hover': {
            background: colors.blue._100,
        },
        '&:active': {
            background: colors.blue._200,
        },
    },
    yellowContrast: {
        background: colors.yellow._500,
        color: colors.black.highEmphasis,
        '&:hover': {
            background: colors.yellow._600,
        },
        '&:active': {
            background: colors.yellow._700,
        },
    },
    yellow: {
        background: colors.yellow._50,
        color: colors.yellow._900,
        '&:hover': {
            background: colors.yellow._100,
        },
        '&:active': {
            background: colors.yellow._200,
        },
    },
    greyContrast: {
        background: colors.neutral._600,
        color: colors.neutral.white,
        '&:hover': {
            background: colors.neutral._700,
        },
        '&:active': {
            background: colors.neutral._800,
        },
    },
    grey: {
        background: colors.neutral._100,
        color: colors.black.highEmphasis,
        '&:hover': {
            background: colors.neutral._200,
        },
        '&:active': {
            background: colors.neutral._300,
        },
    },
    disabled: {
        background: colors.neutral._200,
        color: colors.black.disabled,
    },
    clickable: {
        cursor: 'pointer',
    },
    nonClickable: {
        pointerEvents: 'none',
    },
});

export default useStyles;
